// component.js — base class for RM custom elements.
//
// Provides:
//   • Shadow DOM with adoptedStyleSheets
//   • A render() lifecycle: subclass overrides render() to return a DocumentFragment
//   • Reactive props: declare static props = ["foo", "bar"]; setting `el.foo = …`
//     schedules a re-render. Attribute changes are mirrored to the same setter.
//   • Cleanup: addCleanup(fn) registers a teardown for disconnectedCallback.
//
// Lifecycle:
//   constructor       attaches shadow root
//   connectedCallback first render, sets _connected = true
//   attributeChanged  routes through setter on `props`
//   disconnected      runs cleanup callbacks
//
// This is the single base every screen and widget extends. Identical body to
// KFE web's KFEComponent — kept in sync deliberately. ~120 lines.

/** @typedef {import("../types").Cleanup} Cleanup */

/** @abstract */
export class RMComponent extends HTMLElement {
  /** @type {string[]} - reactive prop names */
  static props = [];
  /** @type {string | null} - shared CSS (applied as <style>) */
  static styles = null;

  /** Optional lifecycle hook; subclasses override.
   * @returns {void | Promise<void>} */
  onConnected() {}
  /** Optional lifecycle hook; subclasses override.
   * @returns {void} */
  onDisconnected() {}

  static get observedAttributes() { return /** @type {any} */ (this).props ?? []; }

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    /** @type {Cleanup[]} */ this._cleanup = [];
    this._connected = false;
    this._renderScheduled = false;
    this._propsInstalled = false;
    // NOTE: reactive prop accessors are installed in `connectedCallback`,
    // not here. Class-field initializers in subclasses run AFTER super()
    // and use [[Define]] semantics, which would clobber any accessor we
    // install in the constructor (TypeError on non-configurable, silent
    // overwrite otherwise). Installing on first connect — after both
    // class-field initializers and any pre-connect property writes from
    // the router (`el[prop] = value`) — captures the right initial value
    // and preserves reactivity for post-mount writes.
  }

  connectedCallback() {
    if (this._connected) return;
    this._connected = true;
    if (!this._propsInstalled) {
      this._installPropAccessors();
      this._propsInstalled = true;
    }
    this._renderNow();
    this.onConnected();
  }

  _installPropAccessors() {
    const ctor = /** @type {typeof RMComponent} */ (this.constructor);
    for (const prop of ctor.props) {
      const internal = "__" + prop;
      // Capture whatever value is currently in the slot — from a class-field
      // initializer (e.g. `listId = null`) or from a pre-connect router
      // assignment (`el.listId = "root"`). Then replace the slot with a
      // reactive accessor that re-renders on change.
      /** @type {any} */ const self = this;
      self[internal] = self[prop];
      Object.defineProperty(self, prop, {
        configurable: true,
        enumerable: true,
        get() { return this[internal]; },
        set(v) {
          if (this[internal] === v) return;
          this[internal] = v;
          this._scheduleRender();
        },
      });
    }
  }

  disconnectedCallback() {
    this._connected = false;
    for (const fn of this._cleanup.splice(0)) {
      try { fn(); } catch (e) { console.error("[rm] cleanup error", e); }
    }
    this.onDisconnected();
  }

  /**
   * @param {string} name
   * @param {string|null} _old
   * @param {string|null} value
   */
  attributeChangedCallback(name, _old, value) {
    /** @type {any} */ (this)[name] = value;
  }

  /** @param {Cleanup} fn */
  addCleanup(fn) { this._cleanup.push(fn); }

  _scheduleRender() {
    if (this._renderScheduled || !this._connected) return;
    this._renderScheduled = true;
    queueMicrotask(() => {
      this._renderScheduled = false;
      this._renderNow();
    });
  }

  _renderNow() {
    const frag = this.render();
    if (frag === undefined) return; // subclass uses imperative render
    const root = this.shadowRoot;
    if (!root) return;
    root.replaceChildren();
    const ctor = /** @type {typeof RMComponent} */ (this.constructor);
    if (ctor.styles) {
      const style = document.createElement("style");
      style.textContent = ctor.styles;
      root.appendChild(style);
    }
    if (frag) root.appendChild(frag);
  }

  /** @returns {DocumentFragment | void} */
  render() { return undefined; }

  /** @template {Event} E @param {string} type @param {EventListener | ((e: E) => void)} fn */
  on(type, fn) {
    this.addEventListener(type, /** @type {EventListener} */ (fn));
    this.addCleanup(() => this.removeEventListener(type, /** @type {EventListener} */ (fn)));
  }

  /** @param {string} type @param {unknown} [detail] */
  emit(type, detail) {
    this.dispatchEvent(new CustomEvent(type, { detail, bubbles: true, composed: true }));
  }
}

/**
 * Define a custom element if not already defined. Idempotent so HMR / re-runs
 * don't blow up.
 * @param {string} tag
 * @param {CustomElementConstructor} cls
 */
export function defineComponent(tag, cls) {
  if (!customElements.get(tag)) customElements.define(tag, cls);
}
